import { Component, EventEmitter, OnInit, Output, ChangeDetectorRef, ViewChild, ElementRef, OnDestroy, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { IntExpContributedService } from './int-exp-contributed.service';

@Component({
    selector: 'gtw-int-exp-contributed',
    templateUrl: './int-exp-contributed.component.html',
    styleUrls: ['./int-exp-contributed.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class IntExpContributedComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  
  @ViewChild('entityDropdownButton', { read: ElementRef }) entityDropdownButton?: ElementRef;
  @ViewChild('entityDropdownMenu', { read: ElementRef }) entityDropdownMenu?: ElementRef;

  // Standard component properties
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;

  // Modal title - matches AngularJS (Add Adjustment / Edit Adjustment)
  modalTitle = "Add Adjustment";

  // Form data
  entity: any = undefined;
  adjustmentName: string = '';
  adjustmentAmount: string = '';
  comments: string = '';

  // Dropdown data
  entityDetails: any[] = [];
  filteredEntityDetails: any[] = [];
  entitySearchTerm: string = '';
  isEntityDropdownOpen: boolean = false;
  dropdownPosition: any = { top: 0, left: 0, width: 0 };
  private clickListener: any;
  private scrollListener: any;
  private resizeListener: any;

  // Edit mode data
  intExpAdjDetails: any[] = [];
  originalIntExpAdjDetails: any[] = []; // Cache for reset without API call
  total: number = 0;

  // Component state
  isCreate = true;
  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  
  // Loading states
  showFormLoader = false;
  entitiesLoading = false;

  private readonly subs = new Subscription();

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private intExpContributedService: IntExpContributedService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {
    // Watch for entity search term changes
    this.watchEntitySearch();
  }
  
  private watchEntitySearch() {
    // Initial filtered list
    this.filteredEntityDetails = this.entityDetails;
  }
  
  // Filter entities based on search term
  filterEntities() {
    if (!this.entitySearchTerm || this.entitySearchTerm.trim() === '') {
      this.filteredEntityDetails = this.entityDetails;
    } else {
      const searchLower = this.entitySearchTerm.toLowerCase();
      this.filteredEntityDetails = this.entityDetails.filter(item => 
        item.VALUE && item.VALUE.toLowerCase().includes(searchLower)
      );
    }
  }
  
  // Toggle entity dropdown
  toggleEntityDropdown() {
    this.isEntityDropdownOpen = !this.isEntityDropdownOpen;
    if (this.isEntityDropdownOpen) {
      this.entitySearchTerm = '';
      this.filteredEntityDetails = this.entityDetails;
      
      // Calculate dropdown position after view updates
      setTimeout(() => {
        this.positionDropdown();
      }, 0);
    }
  }
  
  // Position dropdown using fixed positioning
  positionDropdown() {
    if (!this.entityDropdownButton || !this.entityDropdownMenu) {
      return;
    }
    
    const button = this.entityDropdownButton.nativeElement;
    const dropdown = this.entityDropdownMenu.nativeElement;
    
    if (button && dropdown) {
      // Move dropdown to body to escape any parent transforms
      if (dropdown.parentElement !== document.body) {
        document.body.appendChild(dropdown);
      }
      
      const buttonRect = button.getBoundingClientRect();
      
      // Set dropdown position directly below button, aligned to left edge
      // Using buttonRect directly (already in viewport coordinates)
      const top = buttonRect.bottom + 2;
      const left = buttonRect.left;
      const width = buttonRect.width;
      
      // Apply styles with important to override any conflicts
      dropdown.style.setProperty('top', `${top}px`, 'important');
      dropdown.style.setProperty('left', `${left}px`, 'important');
      dropdown.style.setProperty('width', `${width}px`, 'important');
      dropdown.style.setProperty('min-width', `${width}px`, 'important');
      
      // Check if dropdown goes off bottom of screen
      setTimeout(() => {
        const dropdownHeight = dropdown.offsetHeight;
        const viewportHeight = window.innerHeight;
        
        if (buttonRect.bottom + dropdownHeight > viewportHeight - 10) {
          // Position above button instead
          const topAbove = buttonRect.top - dropdownHeight - 2;
          dropdown.style.setProperty('top', `${topAbove}px`, 'important');
        }
      }, 10);
      
      this.cdr.detectChanges();
    }
  }
  
  // Select entity from dropdown
  selectEntity(item: any) {
    this.entity = item;
    this.isEntityDropdownOpen = false;
    this.entitySearchTerm = '';
    this.cdr.detectChanges();
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();

    // Close dropdown when clicking outside
    this.clickListener = (event: any) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.custom-select-wrapper') && !target.closest('.dropdown-menu-portal')) {
        this.isEntityDropdownOpen = false;
        this.cdr.detectChanges();
      }
    };
    document.addEventListener('click', this.clickListener);
    
    // Reposition dropdown on scroll
    this.scrollListener = () => {
      if (this.isEntityDropdownOpen) {
        this.positionDropdown();
      }
    };
    window.addEventListener('scroll', this.scrollListener, true);
    
    // Reposition dropdown on resize
    this.resizeListener = () => {
      if (this.isEntityDropdownOpen) {
        this.positionDropdown();
      }
    };
    window.addEventListener('resize', this.resizeListener);
  }
  
  ngOnDestroy(): void {
    this.subs.unsubscribe();

    // Clean up event listeners
    if (this.clickListener) {
      document.removeEventListener('click', this.clickListener);
    }
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener, true);
    }
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
    
    // Remove dropdown from body if it was moved there
    if (this.entityDropdownMenu) {
      const dropdown = this.entityDropdownMenu.nativeElement;
      if (dropdown && dropdown.parentElement === document.body) {
        document.body.removeChild(dropdown);
      }
    }
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const fp = normalizeFilterParams(activeScreen) || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const gc = generalConfigFromGlobal(this.gtwGlobal) || {};
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url ?? gc.api ?? '' };
    this.globalService = this.gtwGlobal.hydrateGlobalParamsFromContext(activeScreen, fp) || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = fp;
    this.rowData = rowPayloadFromActiveScreen(activeScreen) || {};
    this.isCreate =
      !this.rowData.COMBINATION_KEY ||
      this.rowData.COMBINATION_KEY === '' ||
      this.rowData.COMBINATION_KEY === null;
    this.modalTitle = this.isCreate ? 'Add Adjustment' : 'Edit Adjustment';
  }

  init() {
    try {
      // CRITICAL: In CREATE mode, rowData might be empty/minimal, so check filterParams only
      const hasFilterParams = this.filterParams && Object.keys(this.filterParams).length > 0;
      const hasRowDataOrIsCreate = this.isCreate || (this.rowData && Object.keys(this.rowData).length > 0);
      
      if (hasFilterParams && hasRowDataOrIsCreate) {
        this.checkLockStatus();
        this.loadData();
      }
    } catch (e) {
      console.error('Error in IntExpContributed init:', e);
    }
  }

  checkLockStatus() {
    const lockParams = {
      action_code: 'n10cpv',
      p_scenario: this.filterParams.scenario,
      p_jcd_key: this.filterParams.jcd_key || this.globalService.jcd_key
    };

    this.intExpContributedService.getJsonObject(
      this.GENERAL_CONFIG.base_url,
      lockParams,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.jsonObject && response.jsonObject.length > 0) {
          const lockData = response.jsonObject[0];
          
          if (lockData.LOCK_STATUS === 'Y') {
            this._snackBar.open(
              `Tax Return has already been filed for tax year ${this.filterParams.tax_year}.`, 
              'Close', 
              { duration: 4000, panelClass: ['error-snackbar'] }
            );
            this.cancel();
            return;
          }
          
          if (lockData.SCENARIO_DESC === 'EXAM') {
            this._snackBar.open(
              'Create Adjustment is not available for selected Scenario.', 
              'Close', 
              { duration: 4000, panelClass: ['error-snackbar'] }
            );
            this.cancel();
            return;
          }
        }
      },
      (error: any) => {
        console.error('Error checking lock status:', error);
      }
    );
  }

  loadData() {
    
    if (!this.isCreate) {
      // EDIT MODE
      this.setEntityFromRowData();
      this.loadEditData();
    } else {
      // CREATE MODE
      this.loadEntityDetails();
    }
  }

  setEntityFromRowData() {
    this.entity = {
      KEY: this.rowData.COMBINATION_KEY,
      VALUE: this.extractEntityNameFromRowData()
    };
    this.entityDetails = [this.entity];
  }

  extractEntityNameFromRowData(): string {
    return this.rowData.ENTITY_NAME || 
           this.rowData.LE_NAME || 
           this.rowData.ENTITY_DESC ||
           this.rowData.COMBINATION_NAME ||
           `Entity ${this.rowData.COMBINATION_KEY}`;
  }

  loadEntityDetails() {
    this.entitiesLoading = true;
    
    const params = {
      action_code: 'g9oqag',
      p_scenario: this.filterParams.scenario,
      p_jcd_key: this.filterParams.jcd_key || this.globalService.jcd_key
    };

    this.intExpContributedService.getJsonObject(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.entitiesLoading = false;
        if (response && response.jsonObject) {
          this.entityDetails = response.jsonObject;
          this.filteredEntityDetails = response.jsonObject; // Initialize filtered list
          this.cdr.detectChanges(); // Force change detection
        }
      },
      (error: any) => {
        this.entitiesLoading = false;
        console.error('Error loading entity details:', error);
      }
    );
  }

  loadEditData() {
    if (!this.rowData.COMBINATION_KEY) {
      return;
    }

    const params = {
      action_code: '75d4wv',
      sso_id: this.USER_SETTINGS.user?.sso_id || '',
      p_tax_year: this.filterParams.tax_year,
      p_scenario: this.filterParams.scenario,
      p_jcd_key: this.filterParams.jcd_key || this.globalService.jcd_key,
      cc_key: this.rowData.COMBINATION_KEY
    };

    this.intExpContributedService.getJsonObject(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.jsonObject) {
          this.intExpAdjDetails = response.jsonObject.map((detail: any) => ({
            ...detail,
            'operation-type': 'U'
          }));
          
          // CRITICAL: Cache original data for reset functionality (avoid redundant API calls)
          this.originalIntExpAdjDetails = JSON.parse(JSON.stringify(this.intExpAdjDetails));
          
          this.updateTotal();
        }
      },
      (error: any) => {
        console.error('Error loading edit data:', error);
      }
    );
  }

  confirmDelete(selectedRowData: any) {
    // Show confirmation dialog matching AngularJS behavior
    const confirmMessage = "Are you sure to remove this Adjustment Type after 'Save'?";
    
    if (confirm(confirmMessage)) {
      this.deleteRow(selectedRowData);
    }
  }

  deleteRow(selectedRowData: any) {
    const index = this.intExpAdjDetails.indexOf(selectedRowData);
    selectedRowData['isDeleted'] = true;
    selectedRowData['operation-type'] = 'D';
    this.intExpAdjDetails[index] = selectedRowData;
    this.updateTotal();
  }

  updatedRow(selectedRowData: any) {
    const index = this.intExpAdjDetails.indexOf(selectedRowData);
    selectedRowData['operation-type'] = 'U';
    this.intExpAdjDetails[index] = selectedRowData;
  }

  updateTotal() {
    this.total = 0;
    this.intExpAdjDetails.forEach((list: any) => {
      if (list['operation-type'] !== 'D') {
        this.total += parseInt(list.AMOUNT ? list.AMOUNT : 0);
      }
    });
  }

  settingAdjustmentAmount(event: any) {
    if (this.adjustmentAmount) {
      this.adjustmentAmount = this.adjustmentAmount.replace(/(?!^-)[^0-9\.]/g, '');
      const amount = this.adjustmentAmount;
      
      if (amount.indexOf('.') !== -1) {
        const amountSp = amount.split('.');
        let bPre = amountSp[0];
        let aPre = amountSp[1];
        
        if (bPre && bPre.length > 20) {
          bPre = bPre.substring(0, 19);
        }
        if (aPre && aPre.length > 2) {
          aPre = aPre.substring(0, 2);
        }
        this.adjustmentAmount = bPre + "." + aPre;
      } else if (amount.length > 20) {
        this.adjustmentAmount = amount.substring(0, amount.length - 1);
      }
    }
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    let returnObj: any = {};
    let transactionDetails: any[] = [];
    let errorInfo = '';
    const otherInfo = {
      sso_id: this.USER_SETTINGS.user?.sso_id || '',
      SCREEN_KEY: this.globalService.screen_key,
      tax_year: this.filterParams.tax_year || this.globalService.tax_year,
      scenario: this.filterParams.scenario || this.globalService.scenario,
      jcd_key: this.filterParams.jcd_key || this.globalService.jcd_key
    };

    // Validation for CREATE mode
    if (this.isCreate) {
      if (!this.entity || !this.entity.KEY ||
          !this.adjustmentName || this.adjustmentName.trim() === '' ||
          this.adjustmentAmount === undefined || this.adjustmentAmount === null || this.adjustmentAmount === '') {
        errorInfo = "Please select all required fields";
        this._snackBar.open(errorInfo, 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.isSaveClicked = false;
        this.crudLoading = false;
        return;
      }

      if (parseFloat(this.adjustmentAmount) === 0) {
        errorInfo = "Adjustment amount should not be 0";
        this._snackBar.open(errorInfo, 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.isSaveClicked = false;
        this.crudLoading = false;
        return;
      }

      // Prepare CREATE data
      returnObj = {
        ENTITY: this.entity.KEY,
        COMBINATION_KEY: this.entity.KEY,
        IS_SINGLE: 'S',
        TRANS_TYPE: 'INT_EXP_ADJ',
        ADJUSTMENT_NAME: this.adjustmentName.trim(),
        AMOUNT: this.adjustmentAmount,
        COMMENTS: this.comments,
        OPERATION_TYPE: 'I'
      };
      transactionDetails.push(returnObj);
    } else {
      // EDIT mode - validate and prepare data
      const hasChanges = this.intExpAdjDetails.some(detail => 
        detail['operation-type'] === 'D' || detail['operation-type'] === 'U'
      );

      if (!hasChanges) {
        this._snackBar.open('No changes to save', 'Close', {
          duration: 4000,
          panelClass: ['warning-snackbar']
        });
        this.isSaveClicked = false;
        this.crudLoading = false;
        return;
      }

      // Validate edit mode data
      for (const details of this.intExpAdjDetails) {
        if (details['operation-type'] === 'D' || details['operation-type'] === 'U') {
          if (!details['ADJ_NAME'] || details['ADJ_NAME'].trim() === '' ||
              details['AMOUNT'] === undefined || details['AMOUNT'] === null || details['AMOUNT'] === '') {
            errorInfo = "Please fill all required fields";
            this._snackBar.open(errorInfo, 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
            this.isSaveClicked = false;
            this.crudLoading = false;
            return;
          }

          const newObject: any = {
            COMBINATION_KEY: this.rowData.COMBINATION_KEY,
            ADJUSTMENT_KEY: details.ADJ_KEY,
            GROUP_KEY: details.ADJ_KEY,
            COMMENTS: details.COMMENTS,
            ADJUSTMENT_NAME: details.ADJ_NAME.trim(),
            AMOUNT: details.AMOUNT,
            OPERATION_TYPE: details['operation-type'],
            TRANS_TYPE: 'INT_EXP_ADJ'
          };
          returnObj.IS_SINGLE = 'S';
          returnObj.TRANS_TYPE = 'INT_EXP_ADJ';
          transactionDetails.push(newObject);
        }
      }
    }

    const lockObj = this.isCreate ? [{combination_key: this.entity.KEY}] : [];

    this.intExpContributedService.saveIntExpContributed(
      this.GENERAL_CONFIG.base_url,
      returnObj,
      transactionDetails,
      otherInfo,
      lockObj,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Interest Expense Adjustment has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  reset() {
    if (this.isCreate) {
      // CREATE mode: Clear form fields (entities already loaded, no API call needed)
      this.entity = undefined;
      this.adjustmentName = '';
      this.adjustmentAmount = '';
      this.comments = '';
    } else {
      // EDIT mode: Restore from cached original data (no API call needed)
      if (this.originalIntExpAdjDetails && this.originalIntExpAdjDetails.length > 0) {
        this.intExpAdjDetails = JSON.parse(JSON.stringify(this.originalIntExpAdjDetails));
        this.updateTotal();
      } else {
        this.loadEditData();
      }
    }
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  compareByKey(item1: any, item2: any): boolean {
    // Handle undefined/null values for "Select..." option
    if (item1 === undefined && item2 === undefined) return true;
    if (item1 === null && item2 === null) return true;
    if (!item1 || !item2) return false;
    return item1.KEY === item2.KEY;
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }
}
